import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Check, Calendar, Award } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MissionCompletionCardProps {
  totalDays: number;
  completedDays: number;
  startedAt: string;
  completedAt?: string;
}

export const MissionCompletionCard: React.FC<MissionCompletionCardProps> = ({
  totalDays,
  completedDays,
  startedAt,
  completedAt
}) => {
  const daysToComplete = completedAt 
    ? Math.ceil((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
      <CardContent className="p-6 space-y-4">
        {/* Header com ícone dourado */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-luxury-gold/30 border border-luxury-gold/30">
            <Trophy className="w-5 h-5 text-luxury-gold" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Jornada Concluída! 🎉
            </h3>
            <p className="text-sm text-muted-foreground">
              Parabéns por completar todas as tarefas
            </p>
          </div>
        </div>

        {/* Feedback estilo BioTopicsDisplayCard */}
        <div className="space-y-3">
          <h4 className="text-base font-medium text-green-400">
            Missão cumprida com excelência!
          </h4>
          
          <p className="text-sm text-muted-foreground leading-relaxed">
            Você completou todos os {totalDays} dias da Jornada Interlaced em apenas {daysToComplete} dias. 
            Agora você receberá um presente exclusivo para usar em seu próximo encontro.
          </p>

          {/* Stats compactos */}
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-white/10 text-sm text-muted-foreground">
            <span className="flex items-center gap-1 text-green-400">
              <Check className="w-3 h-3" />
              {completedDays}/{totalDays} tarefas
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {daysToComplete} dias
            </span>
            {completedAt && (
              <span className="flex items-center gap-1">
                <Award className="w-3 h-3" />
                concluído há {formatDistanceToNow(new Date(completedAt), { locale: ptBR })}
              </span>
            )}
          </div>
        </div>

        {/* Badges de conquistas */}
        <div className="flex flex-wrap gap-2">
          <Badge 
            variant="outline"
            className="bg-purple-900/40 border-purple-500/40 text-purple-200 hover:bg-purple-900/60"
          >
            🎯 Missão Completa
          </Badge>
          <Badge 
            variant="outline"
            className="bg-purple-900/40 border-purple-500/40 text-purple-200 hover:bg-purple-900/60"
          >
            🏆 Recompensa Desbloqueada
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};
